'use client';
// Next.js App Router: global-error.tsx catches errors thrown from the root
// layout itself (when regular error.tsx can't render because its parent
// layout is broken). It must include its own <html> and <body> tags.
//
// Bugsnag is initialised inside BugsnagBoundary which lives in layout.tsx —
// if layout.tsx itself has crashed, Bugsnag may not have initialised. We try
// to notify anyway (the notify call is wrapped in try/catch) and fall back
// to console logging if it isn't available.

import { useEffect } from 'react';
import { notifyBugsnag } from '@/app/error';
import { looksLikeCacheError, STALE_CACHE_GROUPING_HASH } from '@/app/lib/staleClient';

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    const isCacheIssue = looksLikeCacheError(error);
    notifyBugsnag(error, {
      source: 'app/global-error.tsx',
      severity: isCacheIssue ? 'warning' : 'error',
      // Same stable grouping as app/error.tsx: chunk filenames are
      // content-hashed per deploy, so stale-cache errors would otherwise
      // create a fresh Bugsnag group after every release.
      groupingHash: isCacheIssue ? STALE_CACHE_GROUPING_HASH : undefined,
      extra: { cacheIssue: isCacheIssue },
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          minHeight: '100vh',
          fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
          background: '#0a0a0a',
          color: '#f4f4f5',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
        }}
      >
        <main
          style={{
            maxWidth: 560,
            width: '100%',
            padding: '2rem',
            background: '#18181b',
            border: '1px solid #27272a',
            borderRadius: 16,
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }} aria-hidden>
            ⚔️
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: '0 0 0.5rem', letterSpacing: '-0.02em' }}>
            Powston hit a snag
          </h1>
          <p style={{ fontSize: '0.9rem', color: '#a1a1aa', lineHeight: 1.5, margin: '0 0 1.5rem' }}>
            Something broke at the top of the app. We&rsquo;ve logged it and will take a look.
            Try reloading — if it keeps happening, contact support.
          </p>

          {error?.message && (
            <p
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: '0.75rem',
                color: '#71717a',
                background: '#09090b',
                border: '1px solid #27272a',
                borderRadius: 8,
                padding: '0.75rem',
                wordBreak: 'break-word',
                textAlign: 'left',
                margin: '0 0 1.5rem',
              }}
            >
              {error.message}
            </p>
          )}

          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => reset()}
              style={{
                padding: '0.625rem 1.25rem',
                borderRadius: 8,
                border: 'none',
                background: '#d97706',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              Try again
            </button>
            <a
              href="/"
              style={{
                padding: '0.625rem 1.25rem',
                borderRadius: 8,
                background: '#27272a',
                color: '#f4f4f5',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
              }}
            >
              Go home
            </a>
          </div>
        </main>
      </body>
    </html>
  );
}
